import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { VocabPairPoolContent, VocabPoolPair } from "../content-types";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Match a pool of target-language words to their meanings (mirrors iOS
 *  VocabPairPoolView grading: each term maps to its own translation). */
export function VocabPairPoolRenderer({ content, answer, setAnswer, revealed }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as VocabPairPoolContent;
  const pairs: VocabPoolPair[] = c.pairs ?? [];
  const map = (answer as Record<string, string>) ?? {};
  const choices = useMemo(() => {
    const en = pairs.map((p) => p.en);
    for (let i = en.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [en[i], en[j]] = [en[j], en[i]];
    }
    return en;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Prompt>{c.prompt || t("Match each word to its meaning")}</Prompt>
      <div className="mt-4 space-y-3">
        {pairs.map((p) => {
          const chosen = map[p.pair_id] ?? "";
          const ok = revealed ? chosen === p.en : null;
          return (
            <div key={p.pair_id}>
              <div className="mb-1 text-sm font-semibold text-ink">
                {p.de}
                {p.hint && <span className="ml-2 text-xs text-ink-faint">({p.hint})</span>}
              </div>
              <select
                value={chosen}
                disabled={revealed}
                onChange={(e) => setAnswer({ ...map, [p.pair_id]: e.target.value })}
                className={`w-full rounded-xl border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 ${
                  revealed
                    ? ok
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-red-400 bg-red-50"
                    : "border-black/10 bg-white"
                }`}
              >
                <option value="">{t("Choose…")}</option>
                {choices.map((en, i) => (
                  <option key={i} value={en}>{en}</option>
                ))}
              </select>
              {revealed && !ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {t("Correct:")} <span className="font-semibold">{p.en}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>
      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
